import { useCallback, useEffect, useId, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { benefitsGateway, catalogGateway } from '../../benefits-deductions';
import { bdErrorMessage } from '../../benefits-deductions/bdErrors';
import { BENEFIT_CATEGORY_CODES, DEDUCTION_CATEGORY_CODES } from '../../benefits-deductions/categories';
import { DefinitionFormDialog } from '../../benefits-deductions/components/DefinitionFormDialog';
import { PlanFormDialog } from '../../benefits-deductions/components/PlanFormDialog';
import { coverageLevelLabels, type BenefitElection, type CatalogItemType, type CompanyCatalogItem, type CompanyCatalogSource } from '../../benefits-deductions/types';
import { compensationGateway } from '../../compensation';
import type { DeductionElectionResponse } from '../../compensation/types';
import { ConfirmCard, ResultCard } from '../../../shared/components/ConfirmCard';
import { HelpTooltip } from '../../../shared/components/HelpTooltip';
import '../../compensation/compensation.css';
import '../employee-list.css';
import './employee-benefits-deductions.css';
import { CATALOG_HELPER_TEXT, CatalogPicker } from './CatalogPicker';
import {
  benefitBody, benefitRow, contributionText, dayAfter, deductionBody, deductionRow, emptyAssignmentForm, fieldConfig, formFromRow, formatMoney, frequencyLabel, isCurrent,
  isPercentMethod, methodLabel, rowBadge, todayIso, validateAssignment, type AssignmentForm, type AssignmentRow, type FieldMode,
} from './assignmentModel';

const unitOf = (mode: FieldMode) => (mode === 'percent' ? '%' : mode === 'amount' ? '$' : null);

/** A statutory tax is calculated by the system and a garnishment or levy is a court order for one employee, so neither is a company item an Admin can add here. */
const CREATABLE_DEDUCTION_CATEGORIES = DEDUCTION_CATEGORY_CODES.filter((c) => c !== 'taxes-statutory' && c !== 'garnishments-levies');

const EXEMPTION_NOTE =
  'Whether a benefit or deduction is taken before or after tax, and which taxes it reduces, is set by the item’s approved tax configuration. It is shown here, not changed here.';

function EditIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
      <path d="M2.5 13.5h2.8l7.3-7.3-2.8-2.8-7.3 7.3zM11.2 2.8l2 2" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
    </svg>
  );
}

function StopIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
      <circle cx="8" cy="8" r="5.5" fill="none" stroke="currentColor" strokeWidth="1.3" />
      <path d="M4.6 11.4l6.8-6.8" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    </svg>
  );
}

function RowAction({ label, tone, onClick, children, disabled }: { label: string; tone: 'edit' | 'danger'; onClick: () => void; children: ReactNode; disabled?: boolean }) {
  return (
    <button type="button" className={`row-action row-action--${tone}`} aria-label={label} title={label} onClick={onClick} disabled={disabled}>
      {children}
      <span className="row-action__label" aria-hidden="true">{label}</span>
    </button>
  );
}

function Badge({ tone, children, title }: { tone: 'benefit' | 'deduction' | 'pre' | 'post' | 'inactive' | 'setup' | 'waived'; children: ReactNode; title?: string }) {
  return <span className={`bd-tag bd-tag--${tone}`} title={title}>{children}</span>;
}

/** Everything the table needs to name a row, read once from the company catalog (every status, so an item that was deactivated since still has its name). */
async function readCatalogLookup(): Promise<{ items: CompanyCatalogItem[]; sources: CompanyCatalogSource[] }> {
  const items: CompanyCatalogItem[] = [];
  let sources: CompanyCatalogSource[] = [];
  for (let page = 1; page <= 20; page++) {
    const result = await catalogGateway.listItems({ status: 'all', page, pageSize: 100 });
    items.push(...result.items);
    sources = result.sources;
    if (result.items.length === 0 || items.length >= result.totalCount) break;
  }
  return { items, sources };
}

/**
 * Admin Employee Profile Setup, Step 4: Additional Settings > Benefits / Deductions.
 *
 * What is stored, and where:
 *  - the DEFINITION (a benefit plan or a deduction) is a company catalog item owned by Compensation (benefits) or Payroll Configuration (deductions). This screen only reads
 *    it, through the unified catalog; it never edits or creates a definition. Items are made under People > Benefits and Deductions.
 *  - the ASSIGNMENT is an effective-dated election for this employee. There is no in-place edit on the server: Edit saves a new version that starts on the chosen date and
 *    closes the previous one, and Deactivate ends the assignment. Earlier rows are never rewritten or removed, so payroll already posted keeps its figures.
 *
 * Nothing here decides how an item is calculated or taxed: calculation, frequency and tax treatment are the catalog item's own and are shown read-only.
 * Success is shown only after the server confirmed the write.
 */
export function EmployeeBenefitsDeductions({ employeeId }: { employeeId: string }) {
  const [catalog, setCatalog] = useState<CompanyCatalogItem[]>([]);
  const [catalogSources, setCatalogSources] = useState<CompanyCatalogSource[]>([]);
  const [deductionElections, setDeductionElections] = useState<DeductionElectionResponse[]>([]);
  const [benefitElections, setBenefitElections] = useState<BenefitElection[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadErrors, setLoadErrors] = useState<string[]>([]);

  const [editing, setEditing] = useState<AssignmentRow | 'new' | null>(null);
  const [type, setType] = useState<CatalogItemType>('Benefit');
  const [item, setItem] = useState<CompanyCatalogItem | null>(null);
  const [form, setForm] = useState<AssignmentForm>(emptyAssignmentForm);
  const [baseline, setBaseline] = useState<string>('');
  const [shownErrors, setShownErrors] = useState<Record<string, string | undefined>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<'save' | 'discard' | null>(null);
  const [ending, setEnding] = useState<AssignmentRow | null>(null);
  const [endError, setEndError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<{ title: string; text: string } | null>(null);
  const [creating, setCreating] = useState<CatalogItemType | null>(null);
  const [createForbidden, setCreateForbidden] = useState(false);
  /** The item just created from this dialog: it is selected, and says whether it can be assigned yet. */
  const [justCreated, setJustCreated] = useState<{ name: string; assignable: boolean } | null>(null);
  const createLinkRef = useRef<HTMLButtonElement>(null);

  // A stable callback: the card dismisses itself after a few seconds, and a new function on every render would restart that timer each time.
  const dismissNotice = useCallback(() => setNotice(null), []);

  const pickerRef = useRef<HTMLInputElement>(null);
  const addButtonRef = useRef<HTMLButtonElement>(null);
  const modalTitleId = useId();
  const itemLabelId = useId();

  const load = useCallback(async () => {
    const problems: string[] = [];
    const [lookup, deductions, benefits] = await Promise.allSettled([
      readCatalogLookup(), compensationGateway.listDeductionElections(employeeId), benefitsGateway.listEmployeeElections(employeeId),
    ]);
    if (lookup.status === 'fulfilled') {
      setCatalog(lookup.value.items);
      setCatalogSources(lookup.value.sources);
    } else problems.push('The company catalog could not be loaded, so some names may be missing.');
    if (deductions.status === 'fulfilled') setDeductionElections(deductions.value);
    else problems.push('Could not load this employee’s deductions.');
    if (benefits.status === 'fulfilled') setBenefitElections(benefits.value);
    else problems.push('Could not load this employee’s benefits.');
    setLoadErrors(problems);
    setLoading(false);
  }, [employeeId]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void load();
  }, [load]);

  useEffect(() => {
    if (editing === 'new') pickerRef.current?.focus();
  }, [editing]);

  const today = todayIso();
  const catalogByKey = new Map(catalog.map((c) => [c.key, c]));
  const rows: AssignmentRow[] = [
    ...benefitElections.filter((e) => isCurrent(e.effectiveEndDate, today)).map((e) => benefitRow(e, catalogByKey.get(`benefit:${e.benefitCodeId}`))),
    ...deductionElections.filter((e) => isCurrent(e.effectiveEndDate, today)).map((e) => deductionRow(e, catalogByKey.get(`deduction:${e.deductionCodeId}`))),
  ].sort((a, b) => a.start.localeCompare(b.start) || a.key.localeCompare(b.key));

  const isEdit = editing !== null && editing !== 'new';
  const config = item ? fieldConfig(item) : null;
  const dirty = JSON.stringify({ type, item: item?.key ?? null, form }) !== baseline;
  const alreadyAssigned = item !== null && rows.some((r) => r.kind === item.itemType && r.itemId === item.id);
  const checked = validateAssignment(item, form, { today, isEdit, alreadyAssigned });
  const valid = Object.keys(checked.errors).length === 0;
  /** A message from the last save attempt or the server, else a live "wrong value" message (never a bare "required"). */
  const shown = (key: string): string | undefined =>
    shownErrors[key] ?? (checked.missing.has(key as never) ? undefined : (checked.errors as Record<string, string | undefined>)[key]);

  function snapshot(t: CatalogItemType, i: CompanyCatalogItem | null, f: AssignmentForm) {
    return JSON.stringify({ type: t, item: i?.key ?? null, form: f });
  }

  function setField<K extends keyof AssignmentForm>(key: K, value: AssignmentForm[K]) {
    setForm((f) => ({ ...f, [key]: value }));
    setShownErrors((e) => ({ ...e, [key]: undefined }));
    setFormError(null);
  }

  function changeType(next: CatalogItemType) {
    if (next === type) return;
    // The catalog options belong to the type, so the item and everything that came from it are cleared; the date is kept.
    setType(next);
    setItem(null);
    setJustCreated(null);
    setForm((f) => ({ ...emptyAssignmentForm(), effectiveDate: f.effectiveDate }));
    setShownErrors({});
    setFormError(null);
  }

  function chooseItem(chosen: CompanyCatalogItem) {
    setItem(chosen);
    const mode = fieldConfig(chosen);
    // The definition's own default is only a suggestion for the contribution field, and only while it is still empty.
    const suggest = (side: 'employee' | 'employer') => {
      const m = mode[side];
      const value = side === 'employee'
        ? (m === 'percent' ? chosen.defaultEmployeePercent : chosen.defaultEmployeeAmount)
        : (m === 'percent' ? chosen.defaultEmployerPercent : chosen.defaultEmployerAmount);
      return m === 'amount' || m === 'percent' ? (value === null ? '' : String(value)) : '';
    };
    setForm((f) => ({ ...f, employee: f.employee !== '' ? f.employee : suggest('employee'), employer: f.employer !== '' ? f.employer : suggest('employer'), coverageLevel: '' }));
    setShownErrors({});
    setFormError(null);
  }

  function openAdd() {
    const fresh = emptyAssignmentForm();
    setType('Benefit');
    setItem(null);
    setForm(fresh);
    setBaseline(snapshot('Benefit', null, fresh));
    setShownErrors({});
    setFormError(null);
    setEditing('new');
  }

  function openEdit(row: AssignmentRow) {
    if (!row.item) return;
    const f = formFromRow(row, today);
    setType(row.kind);
    setItem(row.item);
    setForm(f);
    setBaseline(snapshot(row.kind, row.item, f));
    setShownErrors({});
    setFormError(null);
    setEditing(row);
  }

  /** The new item is saved in its owning module; read it back through the catalog (so the dialog shows exactly what the server says), select it, and say whether it can be assigned yet. */
  async function itemCreated(kind: CatalogItemType, id: string, name: string) {
    setCreating(null);
    try {
      const page = await catalogGateway.listItems({ type: kind, search: name, status: 'all', pageSize: 100 });
      const found = page.items.find((i) => i.id === id);
      if (found) {
        chooseItem(found);
        setJustCreated({ name: found.name, assignable: found.assignable });
      } else {
        setJustCreated({ name, assignable: false });
      }
    } catch {
      setJustCreated({ name, assignable: false });
    }
    void load();
    setTimeout(() => createLinkRef.current?.focus(), 0);
  }

  function closeModal() {
    setJustCreated(null);
    setEditing(null);
    setConfirm(null);
    setShownErrors({});
    setFormError(null);
    addButtonRef.current?.focus();
  }

  async function write() {
    if (!item) return;
    setBusy(true);
    setFormError(null);
    try {
      // An edit replaces the current assignment from the new date; an add must not (the server refuses a second open one of the same item).
      if (item.itemType === 'Deduction') await compensationGateway.createDeductionElection(employeeId, deductionBody(item, form, isEdit));
      else await benefitsGateway.createElection(employeeId, benefitBody(item, form, isEdit));
      await load();
      setEditing(null);
      setConfirm(null);
      setNotice(
        isEdit
          ? { title: 'Changes Saved!', text: 'Employee’s benefits / deduction has been successfully updated.' }
          : { title: 'Benefit / Deduction Added!', text: 'Employee’s benefits / deduction has been successfully added.' },
      );
      addButtonRef.current?.focus();
    } catch (err) {
      setConfirm(null);
      // The entries are kept so nothing has to be retyped.
      setFormError(bdErrorMessage(err, 'Could not save. Nothing was changed.', item.itemType === 'Deduction' ? 'assignment' : 'election'));
      await load().catch(() => undefined);
    } finally {
      setBusy(false);
    }
  }

  function submit(e: FormEvent) {
    e.preventDefault();
    if (busy) return;
    if (!valid) {
      setShownErrors(checked.errors);
      return;
    }
    if (!isEdit) {
      void write();
      return;
    }
    if (!dirty) {
      closeModal();
      return;
    }
    setConfirm('save');
  }

  function discard() {
    if (!dirty) {
      closeModal();
      return;
    }
    setConfirm('discard');
  }

  async function confirmEnd() {
    if (!ending) return;
    setBusy(true);
    setEndError(null);
    try {
      // The server only accepts an end date after the start date, so an assignment that starts today ends tomorrow and is shown with "Ends …" until then.
      const end = ending.start < today ? today : dayAfter(ending.start);
      if (ending.kind === 'Deduction') await compensationGateway.endDeductionElection(ending.electionId, end, ending.concurrencyVersion);
      else await benefitsGateway.endElection(ending.electionId, end, ending.concurrencyVersion);
      await load();
      setEnding(null);
      setNotice({ title: 'Benefit / Deduction Deactivated!', text: 'Employee’s benefit / deduction has been successfully deactivated. Earlier payroll is unchanged.' });
    } catch (err) {
      setEndError(bdErrorMessage(err, 'Could not deactivate this item. Nothing was changed.', 'election'));
    } finally {
      setBusy(false);
    }
  }

  const halfProblems = catalogSources.filter((s) => s.status !== 'Included');
  const itemInvalid = shown('item') !== undefined;
  const employeeUnit = config ? unitOf(config.employee) : null;
  const employerUnit = config ? unitOf(config.employer) : null;

  return (
    <div className="bd employee-list">
      <div className="bd-toolbar">
        <button ref={addButtonRef} type="button" className="bd-add" onClick={openAdd}>+ Add Benefit / Deduction</button>
      </div>

      {loadErrors.map((m) => <p key={m} className="error" role="alert">{m}</p>)}
      {halfProblems.map((s) => (
        <p key={s.itemType} className="muted" role="status">
          {s.status === 'NoPermission'
            ? `You don’t have permission to view the company’s ${s.itemType.toLowerCase()}s, so those rows may not show a name.`
            : `The company’s ${s.itemType.toLowerCase()}s could not be loaded, so those rows may not show a name.`}
        </p>
      ))}

      <div className="employee-list__table-wrap bd-table-wrap">
        <table className="employee-list__table bd-table">
          <thead>
            <tr>
              <th scope="col">Type</th>
              <th scope="col">Name</th>
              <th scope="col">Category</th>
              <th scope="col">Tax Treatment <HelpTooltip label="About tax treatment" text={EXEMPTION_NOTE} /></th>
              <th scope="col">Employee Contribution</th>
              <th scope="col">Employer Contribution</th>
              <th scope="col">Calculation</th>
              <th scope="col">Frequency</th>
              <th scope="col">Effective Date</th>
              <th scope="col">Status</th>
              <th scope="col">Action</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={11} className="bd-empty">Loading…</td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={11} className="bd-empty">
                  <svg width="34" height="34" viewBox="0 0 40 40" aria-hidden="true" focusable="false">
                    <path d="M4 10a2 2 0 0 1 2-2h9l3 4h16a2 2 0 0 1 2 2v16a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2z" fill="currentColor" />
                  </svg>
                  <div>No Data Available</div>
                </td>
              </tr>
            )}
            {rows.map((r) => {
              const badge = rowBadge(r.item);
              const treatment = r.item?.taxTreatment;
              return (
                <tr key={r.key}>
                  <td><Badge tone={r.kind === 'Benefit' ? 'benefit' : 'deduction'}>{r.kind}</Badge></td>
                  <td>
                    {r.item?.name ?? 'Unknown item'}
                    {r.title && <span className="muted bd-ends"> {r.title}</span>}
                  </td>
                  <td>{r.item?.categoryName ?? '—'}</td>
                  <td>
                    {treatment === 'PreTax' && <Badge tone="pre">Pre-tax</Badge>}
                    {treatment === 'PostTax' && <Badge tone="post">Post-tax</Badge>}
                    {!treatment && <span className="muted" title="Set by this item’s approved tax profile">{r.item?.taxProfileCode ? 'Per tax profile' : '—'}</span>}
                  </td>
                  <td>
                    {r.isWaived ? 'Waived' : contributionText(r.employee)}
                    {r.annualLimit !== null && <span className="muted bd-ends"> Limit {formatMoney(r.annualLimit)} a year</span>}
                  </td>
                  <td>{r.isWaived ? '—' : contributionText(r.employer)}</td>
                  <td>{r.item ? methodLabel(r.item.calculationMethod) : '—'}{r.coverageLevel && <span className="muted bd-ends"> {coverageLevelLabels[r.coverageLevel]}</span>}</td>
                  <td>{r.item ? frequencyLabel(r.item.frequency) : '—'}</td>
                  <td>{r.start}</td>
                  <td>
                    {r.isWaived && <Badge tone="waived">Waived</Badge>}
                    {badge === 'Inactive' && <Badge tone="inactive" title="This item is inactive, so it is not applied to payroll.">Inactive</Badge>}
                    {badge === 'Requires payroll setup' && <Badge tone="setup" title={r.item?.notAssignableReason ?? 'Payroll has not finished setting this up, so it is not applied to payroll.'}>Requires payroll setup</Badge>}
                    {r.start > today && <span className="muted bd-ends"> Starts {r.start}</span>}
                    {r.end && <span className="muted bd-ends"> Ends {r.end}</span>}
                    {!badge && !r.isWaived && !r.end && r.start <= today && <span className="muted">Active</span>}
                  </td>
                  <td>
                    <span className="employee-list__actions">
                      {!r.isWaived && <RowAction label="Edit" tone="edit" disabled={!r.item} onClick={() => openEdit(r)}><EditIcon /></RowAction>}
                      <RowAction label="Deactivate" tone="danger" onClick={() => { setEndError(null); setEnding(r); }}><StopIcon /></RowAction>
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="compensation-modal-backdrop" role="presentation">
          <div className="compensation-modal bd-modal" role="dialog" aria-modal="true" aria-labelledby={modalTitleId}>
            <div className="compensation-modal-head">
              <h3 id={modalTitleId}>Benefits / Deductions</h3>
              <button type="button" className="compensation-modal-close" aria-label="Close" onClick={discard} disabled={busy}>×</button>
            </div>
            <form onSubmit={submit} noValidate>
              <div className="compensation-modal-body">
                <fieldset className="bd-fieldset" disabled={isEdit || busy}>
                  <legend>Type <span aria-hidden="true">*</span></legend>
                  <div className="bd-segmented" role="radiogroup" aria-label="Type">
                    {(['Benefit', 'Deduction'] as const).map((t) => (
                      <label key={t} className={`bd-segmented__option${type === t ? ' bd-segmented__option--on' : ''}`}>
                        <input type="radio" name="bd-item-type" value={t} checked={type === t} onChange={() => changeType(t)} />
                        {t}
                      </label>
                    ))}
                  </div>
                </fieldset>

                <div className="bd-field">
                  <span id={itemLabelId} className="bd-field__label">Company benefit or deduction <span aria-hidden="true">*</span></span>
                  <CatalogPicker type={type} value={item} onChange={chooseItem} disabled={isEdit || busy} invalid={itemInvalid} inputRef={pickerRef} labelledBy={itemLabelId} />
                  <span className="muted bd-help">{CATALOG_HELPER_TEXT}</span>
                  {!isEdit && !createForbidden && (
                    <button ref={createLinkRef} type="button" className="bd-create-link" disabled={busy} onClick={() => setCreating(type)}>
                      + Create new company {type === 'Benefit' ? 'benefit' : 'deduction'}
                    </button>
                  )}
                  {createForbidden && <span className="muted bd-help" role="status">You don’t have permission to create company benefits or deductions. Ask an administrator to add it.</span>}
                  {justCreated && (
                    <p className="bd-notice bd-notice--info" role="status">
                      {justCreated.assignable
                        ? `“${justCreated.name}” was saved to the company catalog and is selected below.`
                        : `“${justCreated.name}” was saved to the company catalog and is selected, but it is waiting for tax review. It appears under People > Benefits and Deductions, and it can be assigned to employees as soon as its tax treatment is approved.`}
                    </p>
                  )}
                  {shown('item') && <span className="error" role="alert">{shown('item')}</span>}
                </div>

                {item && config && (
                  <>
                    <dl className="bd-summary" aria-label="About this item">
                      <div><dt>Category</dt><dd>{item.categoryName}</dd></div>
                      <div>
                        <dt>Tax treatment <HelpTooltip label="About tax treatment" text={EXEMPTION_NOTE} /></dt>
                        <dd>
                          {item.taxTreatment === 'PreTax' && <Badge tone="pre">Pre-tax</Badge>}
                          {item.taxTreatment === 'PostTax' && <Badge tone="post">Post-tax</Badge>}
                          {!item.taxTreatment && (item.taxProfileCode ? 'Per its tax profile' : 'Not set yet')}
                        </dd>
                      </div>
                      <div><dt>Calculation <HelpTooltip label="About calculation" text="How this item is calculated is part of the company item. It is shown here and is not changed for one employee." /></dt><dd>{methodLabel(item.calculationMethod)}</dd></div>
                      <div><dt>Frequency <HelpTooltip label="About frequency" text="How often this item is taken follows the company item and the employee’s pay schedule." /></dt><dd>{frequencyLabel(item.frequency)}</dd></div>
                    </dl>

                    {config.noPayrollContribution && (
                      <p className="muted" role="note">This plan has no payroll contribution. Assigning it records the employee’s enrollment only.</p>
                    )}

                    {config.needsCoverage && (
                      <label>
                        Coverage Level <span aria-hidden="true">*</span>
                        <select
                          value={form.coverageLevel}
                          disabled={busy}
                          aria-required="true"
                          aria-invalid={shown('coverageLevel') ? true : undefined}
                          onChange={(e) => setField('coverageLevel', e.target.value as AssignmentForm['coverageLevel'])}
                        >
                          <option value="">Select a coverage level</option>
                          {item.coverageLevels.map((level) => <option key={level} value={level}>{coverageLevelLabels[level]}</option>)}
                        </select>
                        <span className="muted bd-help">Each level has its own contribution, set on the plan.</span>
                        {shown('coverageLevel') && <span className="error" role="alert">{shown('coverageLevel')}</span>}
                      </label>
                    )}

                    {(config.employee === 'amount' || config.employee === 'percent' || config.employer === 'amount' || config.employer === 'percent') && (
                      <div className="bd-row">
                        {(config.employee === 'amount' || config.employee === 'percent') && (
                          <label>
                            Employee Contribution{employeeUnit ? ` (${employeeUnit})` : ''} {item.itemType === 'Deduction' && <span aria-hidden="true">*</span>}
                            <input
                              inputMode="decimal"
                              autoComplete="off"
                              placeholder={config.employee === 'percent' ? '0' : '0.00'}
                              value={form.employee}
                              disabled={busy}
                              aria-required={item.itemType === 'Deduction' || undefined}
                              aria-invalid={shown('employee') ? true : undefined}
                              onChange={(e) => setField('employee', e.target.value)}
                            />
                            <span className="muted bd-help">
                              {item.itemType === 'Deduction'
                                ? 'What is taken from this employee’s pay each time.'
                                : 'Leave empty to use the plan’s own contribution.'}
                            </span>
                            {shown('employee') && <span className="error" role="alert">{shown('employee')}</span>}
                          </label>
                        )}
                        {(config.employer === 'amount' || config.employer === 'percent') && (
                          <label>
                            Employer Contribution{employerUnit ? ` (${employerUnit})` : ''}
                            <input
                              inputMode="decimal"
                              autoComplete="off"
                              placeholder={config.employer === 'percent' ? '0' : '0.00'}
                              value={form.employer}
                              disabled={busy}
                              aria-invalid={shown('employer') ? true : undefined}
                              onChange={(e) => setField('employer', e.target.value)}
                            />
                            <span className="muted bd-help">What the company adds. Leave empty for none{item.itemType === 'Benefit' ? ' beyond the plan’s own' : ''}.</span>
                            {shown('employer') && <span className="error" role="alert">{shown('employer')}</span>}
                          </label>
                        )}
                      </div>
                    )}

                    {item.itemType === 'Deduction' && (
                      <label>
                        Title
                        <input
                          value={form.title}
                          maxLength={100}
                          autoComplete="off"
                          disabled={busy}
                          aria-invalid={shown('title') ? true : undefined}
                          onChange={(e) => setField('title', e.target.value)}
                        />
                        <span className="muted bd-help">An optional label for this employee’s assignment, such as a case or loan number. It is for display only.</span>
                        {shown('title') && <span className="error" role="alert">{shown('title')}</span>}
                      </label>
                    )}

                    <label>
                      {isEdit ? 'Changes take effect on' : 'Effective Date'} <span aria-hidden="true">*</span>
                      <input
                        type="date"
                        value={form.effectiveDate}
                        min={isEdit ? today : undefined}
                        disabled={busy}
                        aria-required="true"
                        aria-invalid={shown('effectiveDate') ? true : undefined}
                        onChange={(e) => setField('effectiveDate', e.target.value)}
                      />
                      <span className="muted bd-help">
                        {isEdit
                          ? 'The new version starts on this date and the current one ends then. Earlier payroll is never changed.'
                          : 'The date this starts applying. It cannot change payroll that is already posted.'}
                      </span>
                      {shown('effectiveDate') && <span className="error" role="alert">{shown('effectiveDate')}</span>}
                    </label>

                    <label>
                      End Date
                      <input
                        type="date"
                        value={form.endDate}
                        min={form.effectiveDate || undefined}
                        disabled={busy}
                        aria-invalid={shown('endDate') ? true : undefined}
                        onChange={(e) => setField('endDate', e.target.value)}
                      />
                      <span className="muted bd-help">Optional. Leave it empty if this has no end date. You can also deactivate it later.</span>
                      {shown('endDate') && <span className="error" role="alert">{shown('endDate')}</span>}
                    </label>

                    {item.itemType === 'Deduction' && (
                      <label>
                        Employee Annual Limit ($)
                        <input
                          inputMode="decimal"
                          autoComplete="off"
                          placeholder="0.00"
                          value={form.annualLimit}
                          disabled={busy}
                          aria-invalid={shown('annualLimit') ? true : undefined}
                          onChange={(e) => setField('annualLimit', e.target.value)}
                        />
                        <span className="muted bd-help">Optional. The most to take from this employee in a year. It cannot be more than the limit on the company item. It is recorded here; payroll does not apply limits yet.</span>
                        {shown('annualLimit') && <span className="error" role="alert">{shown('annualLimit')}</span>}
                      </label>
                    )}

                    {!item.assignable && item.notAssignableReason && <p className="error" role="alert">{item.notAssignableReason}</p>}
                    {isPercentMethod(item) && item.itemType === 'Deduction' && (
                      <p className="muted" role="note">This deduction is a percentage, so the contribution fields are percentages.</p>
                    )}
                  </>
                )}
                {formError && <p className="error" role="alert">{formError}</p>}
              </div>
              <div className="compensation-modal-footer">
                {isEdit ? (
                  <>
                    <button type="button" className="bd-btn bd-btn--danger" onClick={discard} disabled={busy}>Discard</button>
                    <button type="submit" className="bd-btn bd-btn--save" disabled={busy || !valid}>{busy ? 'Saving…' : 'Save'}</button>
                  </>
                ) : (
                  <button type="submit" className="bd-btn bd-btn--save" disabled={busy || !valid}>{busy ? 'Saving…' : 'Save'}</button>
                )}
              </div>
            </form>
          </div>
        </div>
      )}

      {confirm === 'save' && (
        <ConfirmCard
          title="Save Changes?"
          body="You’ve made changes to this employee’s benefits / deduction. Do you want to save your updates?"
          cancelLabel="Keep Editing"
          confirmLabel="Save Edit"
          confirmTone="save"
          busy={busy}
          onCancel={() => setConfirm(null)}
          onConfirm={() => void write()}
        />
      )}
      {confirm === 'discard' && (
        <ConfirmCard
          title="Discard Changes?"
          body="You’ve made changes to this employee’s benefits / deduction. Do you want to discard your updates?"
          cancelLabel="Keep Editing"
          confirmLabel="Discard Edit"
          confirmTone="danger"
          busy={false}
          onCancel={() => setConfirm(null)}
          onConfirm={() => {
            closeModal();
            setNotice({ title: 'Changes Discarded!', text: 'Your unsaved changes were discarded. The saved benefit / deduction has not been changed.' });
          }}
        />
      )}
      {ending && (
        <ConfirmCard
          title="Are you sure?"
          body="Are you sure you want to deactivate this benefit / deduction for this employee? It stops applying going forward. Other employees and earlier payroll are not changed."
          cancelLabel="Cancel"
          confirmLabel="Yes, Deactivate"
          confirmTone="danger"
          busy={busy}
          error={endError}
          onCancel={() => setEnding(null)}
          onConfirm={() => void confirmEnd()}
        />
      )}

      {creating === 'Benefit' && (
        <PlanFormDialog
          mode="create" category="other-custom" categoryChoices={[...BENEFIT_CATEGORY_CODES]} requireDeclaredTax
          onClose={() => { setCreating(null); setTimeout(() => createLinkRef.current?.focus(), 0); }}
          onSaved={(plan) => void itemCreated('Benefit', plan.id, plan.name)}
          onForbidden={() => { setCreateForbidden(true); setCreating(null); }}
        />
      )}
      {creating === 'Deduction' && (
        <DefinitionFormDialog
          mode="create" category="other-custom" categoryChoices={CREATABLE_DEDUCTION_CATEGORIES} requireDeclaredTax
          onClose={() => { setCreating(null); setTimeout(() => createLinkRef.current?.focus(), 0); }}
          onSaved={(definition) => void itemCreated('Deduction', definition.id, definition.name)}
          onForbidden={() => { setCreateForbidden(true); setCreating(null); }}
        />
      )}

      {notice && <ResultCard title={notice.title} text={notice.text} onDismiss={dismissNotice} />}
    </div>
  );
}
